import type { AttackProfile, Skill } from '../content/types.ts';
import type { CustomSkillLibrary } from '../domain/customSkill.ts';
import {
  createCustomSkillLibrary,
  migrateCustomSkillLibraryRecord,
  validateCustomSkillLibrary,
} from '../domain/customSkill.ts';
import type { StoragePort } from './storagePort.ts';
import { WriteQueue } from './writeQueue.ts';
import { CustomContentRepository } from './customContentRepository.ts';

export type { LibraryStatus, LoadedLibrary } from './customContentRepository.ts';

/**
 * The player's own Skill definitions.
 *
 * Everything about reading, the write lock on an unreadable file, validation
 * and the write queue comes from `CustomContentRepository`. This only supplies
 * what is specific to Skills, so the protections cannot drift apart from the
 * ones custom Backgrounds get.
 */
export class CustomSkillRepository extends CustomContentRepository<CustomSkillLibrary> {
  constructor(
    storage: StoragePort,
    now: () => string = () => new Date().toISOString(),
    queue: WriteQueue = new WriteQueue(),
  ) {
    super(
      {
        name: 'custom-skills',
        label: 'custom Skill',
        createEmpty: createCustomSkillLibrary,
        migrate: migrateCustomSkillLibraryRecord,
        validate: validateCustomSkillLibrary,
      },
      storage,
      now,
      queue,
    );
  }

  /**
   * Replaces the complete Skill-definition payload in ONE repository write.
   * Skill and AttackProfile are one logical definition and one JSON record.
   */
  async saveDefinitions(
    library: CustomSkillLibrary,
    skills: readonly Skill[],
    attacks: readonly AttackProfile[],
  ): Promise<CustomSkillLibrary> {
    return this.save({ ...library, skills: [...skills], attacks: [...attacks] });
  }

  /**
   * Legacy convenience. Deliberately preserves the existing Attack companions;
   * new code should prefer `saveDefinitions` when a definition may affect both.
   */
  async saveSkills(
    library: CustomSkillLibrary,
    skills: readonly Skill[],
  ): Promise<CustomSkillLibrary> {
    return this.saveDefinitions(library, skills, library.attacks);
  }
}
